/**
 * Tokenizers for the Feature<->flag matcher (matchFeatureFlags.ts).
 *
 * Two tokenizers because the two sides of the match use different naming
 * conventions: flag/killswitch ids are engineer-coined SCREAMING_SNAKE_CASE
 * or camelCase identifiers; Feature labels and flag descriptions are prose.
 *
 * @see docs/superpowers/specs/2026-08-09-atlas-flag-cluster-linking-design.md §1
 */

/** Connector words dropped from prose tokenization — small, closed, doesn't grow with the doc corpus (IDF handles the rest). */
const CONNECTOR_WORDS = new Set([
  'and', 'or', 'the', 'of', 'for', 'with', 'a', 'an', 'to', 'in', 'on',
]);

/** Split `SCREAMING_SNAKE_CASE` / `camelCase` identifiers into lowercase tokens, dropping tokens <= 2 chars. */
export function tokenizeIdentifier(id: string): string[] {
  const bySeparator = id.split('_');
  const tokens: string[] = [];
  for (const part of bySeparator) {
    if (part === '') continue;
    // Split camelCase on lower->upper boundaries, then lowercase everything.
    const camelSplit = part.split(/(?<=[a-z])(?=[A-Z])/);
    for (const piece of camelSplit) {
      if (piece.length === 0) continue;
      tokens.push(piece.toLowerCase());
    }
  }
  return tokens.filter((t) => t.length > 2);
}

/** Split free-text prose into lowercase tokens, dropping connector words. */
export function tokenizeProse(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0 && !CONNECTOR_WORDS.has(t));
}
